import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimeSubscription
 * Subscribes to Supabase postgres_changes on a given table.
 * Automatically cleans up the channel on unmount.
 */
const useRealtimeSubscription = ({
  channelName,
  table,
  event = '*',
  filter,
  onInsert,
  onUpdate,
  onDelete,
  onChange,
  enabled = true,
}) => {
  const channelRef = useRef(null);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // Keep callbacks in refs so subscription doesn't need to re-run when they change
  const onChangeRef = useRef(onChange);
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onInsertRef.current = onInsert; }, [onInsert]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onDeleteRef.current = onDelete; }, [onDelete]);

  const cleanup = useCallback(() => {
    if (retryTimerRef?.current) {
      clearTimeout(retryTimerRef?.current);
      retryTimerRef.current = null;
    }
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }
  }, []);

  const subscribe = useCallback(() => {
    if (!mountedRef?.current || !enabled || !table) return;

    // Remove any existing channel first
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }

    const config = { event, schema: 'public', table };
    if (filter) config.filter = filter;

    const channel = supabase
      ?.channel(channelName)
      ?.on('postgres_changes', config, (payload) => {
        onChangeRef?.current?.(payload);
        if (payload?.eventType === 'INSERT') onInsertRef?.current?.(payload);
        if (payload?.eventType === 'UPDATE') onUpdateRef?.current?.(payload);
        if (payload?.eventType === 'DELETE') onDeleteRef?.current?.(payload);
      })
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.debug(`[Realtime] Subscribed to ${channelName}`);
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`[Realtime] ${status} on ${channelName} — retrying in 5s`);
          // Retry after 5 seconds
          if (mountedRef?.current) {
            retryTimerRef.current = setTimeout(() => {
              if (mountedRef?.current) subscribe();
            }, 5000);
          }
        }
      });

    channelRef.current = channel;
  }, [channelName, table, event, filter, enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && table) subscribe();

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [channelName, table, event, filter, enabled]);

  return { cleanup };
};

export default useRealtimeSubscription;
